import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  Grid,
} from "@mui/material";
import { Visibility } from "@mui/icons-material";
import { useEvaluations } from "../../hooks/useEvaluations";
import {
  DIFFICULTY_LABELS,
  DIFFICULTIES,
  EvaluationFilters,
  EvaluationListItem,
} from "../../types";
import EvaluationDetailDialog from "./EvaluationDetailDialog";
import TopicBadges from "../common/TopicBadges";

/**
 * 評価バージョンの表示ラベルを組み立てる。
 *
 * Firestore に保存された evaluationVersion をそのまま表示する。
 * 詳細ビューを持たないバージョン（"unsupported"）は「未対応」を添える。
 * その内訳は、マッパー未追加の新バージョン（evaluationVersion あり）と、
 * 表示対応を終了した v1系レガシー（evaluationVersion なし）の2通り。
 */
const buildEvaluationVersionLabel = (evaluation: EvaluationListItem): string => {
  if (evaluation.version === "unsupported") {
    return evaluation.evaluationVersion
      ? `eval v${evaluation.evaluationVersion}（未対応）`
      : "eval 旧形式（未対応）";
  }
  return `eval v${evaluation.evaluationVersion}`;
};

const EvaluationList: React.FC = () => {
  const { evaluations, loading, error, hasMore, fetchEvaluations, loadMore } = useEvaluations();
  const [selectedEvaluationId, setSelectedEvaluationId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // フィルター状態
  const [startDateStr, setStartDateStr] = useState<string>("");
  const [endDateStr, setEndDateStr] = useState<string>("");
  const [difficulty, setDifficulty] = useState<string>("");

  // 初回ロード
  useEffect(() => {
    fetchEvaluations();
  }, [fetchEvaluations]);

  const handleOpenDialog = (evaluation: EvaluationListItem) => {
    setSelectedEvaluationId(evaluation.id);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedEvaluationId(null);
  };

  const handleApplyFilters = () => {
    const newFilters: EvaluationFilters = {};

    if (startDateStr) {
      newFilters.startDate = new Date(startDateStr);
    }
    if (endDateStr) {
      newFilters.endDate = new Date(endDateStr);
    }
    if (difficulty) {
      newFilters.difficulty = parseInt(difficulty, 10);
    }

    fetchEvaluations(newFilters);
  };

  const handleClearFilters = () => {
    setStartDateStr("");
    setEndDateStr("");
    setDifficulty("");
    fetchEvaluations({});
  };

  const getScoreColor = (
    score: number
  ): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    if (score >= 80) return "success";
    if (score >= 60) return "primary";
    if (score >= 40) return "warning";
    return "error";
  };

  return (
    <Box>
      {/* フィルターカード */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            フィルター
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label="開始日"
                type="date"
                value={startDateStr}
                onChange={(e) => setStartDateStr(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label="終了日"
                type="date"
                value={endDateStr}
                onChange={(e) => setEndDateStr(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select
                label="難易度"
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                fullWidth
              >
                <MenuItem value="">すべて</MenuItem>
                {DIFFICULTIES.map((diff) => (
                  <MenuItem key={diff} value={diff.toString()}>
                    {DIFFICULTY_LABELS[diff]}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box display="flex" gap={1}>
                <Button variant="contained" onClick={handleApplyFilters}>
                  適用
                </Button>
                <Button variant="outlined" onClick={handleClearFilters}>
                  クリア
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 一覧カード */}
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            評価結果一覧
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            ユーザーの回答評価結果の一覧です
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TableContainer component={Paper} sx={{ mt: 2, overflowX: "auto", width: "100%" }}>
            <Table sx={{ minWidth: 800 }}>
              <TableHead>
                <TableRow>
                  <TableCell>日時</TableCell>
                  <TableCell align="center">スコア</TableCell>
                  <TableCell>問題</TableCell>
                  <TableCell>ユーザー回答（プレビュー）</TableCell>
                  <TableCell>バージョン</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {evaluations.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      評価データがありません
                    </TableCell>
                  </TableRow>
                ) : (
                  evaluations.map((evaluation) => (
                    <TableRow key={evaluation.id} hover>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {new Date(evaluation.timestamp._seconds * 1000).toLocaleString("ja-JP")}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${evaluation.totalScore}点`}
                          color={getScoreColor(evaluation.totalScore)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <TopicBadges
                            difficulty={evaluation.topic.difficulty}
                            genre={evaluation.topic.genre}
                            questionType={evaluation.topic.questionType}
                            claimType={evaluation.topic.claimType}
                            sx={{ mb: 0.5 }}
                          />
                          {evaluation.topic.theme && (
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 0.5 }}>
                              {evaluation.topic.theme}
                            </Typography>
                          )}
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mt: 0.5,
                              maxWidth: 250,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                            title={evaluation.topic.content}
                          >
                            {evaluation.topic.content.length > 80
                              ? evaluation.topic.content.substring(0, 80) + "..."
                              : evaluation.topic.content}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 300,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {evaluation.userOpinionPreview}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="flex-start"
                          gap={0.5}
                        >
                          {evaluation.appVersion && (
                            <Chip
                              label={`app v${evaluation.appVersion}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                          <Chip
                            label={buildEvaluationVersionLabel(evaluation)}
                            size="small"
                            variant="outlined"
                            color={evaluation.version === "unsupported" ? "error" : "secondary"}
                          />
                          {evaluation.promptVersion !== undefined && (
                            <Chip
                              label={`prompt v${evaluation.promptVersion}`}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleOpenDialog(evaluation)}
                        >
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ローディング・追加読み込み */}
          <Box display="flex" justifyContent="center" mt={2}>
            {loading ? (
              <CircularProgress />
            ) : hasMore ? (
              <Button variant="outlined" onClick={loadMore}>
                さらに読み込む
              </Button>
            ) : evaluations.length > 0 ? (
              <Typography variant="body2" color="text.secondary">
                すべてのデータを表示しました
              </Typography>
            ) : null}
          </Box>
        </CardContent>
      </Card>

      {/* 詳細ダイアログ */}
      <EvaluationDetailDialog
        open={dialogOpen}
        evaluationId={selectedEvaluationId}
        onClose={handleCloseDialog}
      />
    </Box>
  );
};

export default EvaluationList;
